import { ElementRef } from '@angular/core';

interface FormatDistanceOptions {
  addSuffix?: boolean;
  includeSeconds?: boolean;
}

function formatDistance(date, baseDate, options: FormatDistanceOptions = {}) {
  const { includeSeconds = false, addSuffix = false } = options;

  const MINUTE_IN_MS = 60 * 1000;
  const HOUR_IN_MS = 60 * MINUTE_IN_MS;
  const DAY_IN_MS = 24 * HOUR_IN_MS;
  const MONTH_IN_MS = 30 * DAY_IN_MS;
  const YEAR_IN_MS = 365 * DAY_IN_MS;

  const elapsed = Math.abs(date - baseDate);

  if (includeSeconds && elapsed < 1000) {
    return elapsed < 500 ? 'less than a second' : '1 second';
  }

  if (elapsed < MINUTE_IN_MS) {
    const seconds = Math.round(elapsed / 1000);
    return addSuffix ? `${seconds} second${seconds !== 1 ? 's' : ''} ago` : `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }

  if (elapsed < HOUR_IN_MS) {
    const minutes = Math.round(elapsed / MINUTE_IN_MS);
    return addSuffix ? `${minutes} minute${minutes !== 1 ? 's' : ''} ago` : `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  if (elapsed < DAY_IN_MS) {
    const hours = Math.round(elapsed / HOUR_IN_MS);
    return addSuffix ? `${hours} hour${hours !== 1 ? 's' : ''} ago` : `${hours} hour${hours !== 1 ? 's' : ''}`;
  }

  if (elapsed < MONTH_IN_MS) {
    const days = Math.round(elapsed / DAY_IN_MS);
    return addSuffix ? `${days} day${days !== 1 ? 's' : ''} ago` : `${days} day${days !== 1 ? 's' : ''}`;
  }

  if (elapsed < YEAR_IN_MS) {
    const months = Math.round(elapsed / MONTH_IN_MS);
    return addSuffix ? `${months} month${months !== 1 ? 's' : ''} ago` : `${months} month${months !== 1 ? 's' : ''}`;
  }

  const years = Math.round(elapsed / YEAR_IN_MS);
  return addSuffix ? `${years} year${years !== 1 ? 's' : ''} ago` : `${years} year${years !== 1 ? 's' : ''}`;
}

function parseISO(str: string): Date {
  const dateParts = str.split('-');
  if (dateParts.length === 3) {
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1;
    const day = parseInt(dateParts[2].substring(0, 2), 10);
    return new Date(year, month, day);
  }
  throw new Error('Invalid ISO date string');
}

export class HomeWidget {
  private static readonly spanFmt = {
    'records time': 'Records of all time',
    'records time to': 'Since start of recording until {to}',
    'records time from': 'Records since {from} until now',
    'records time from to': 'Records since {from} until {to}',
    'records time delta': 'Starting from {delta}'
  };

  constructor(public elRef: ElementRef) {}

  public homeWidgetDirective;

  public get settings() {
    return this.homeWidgetDirective.settings;
  }

  public get keyDate() {
    return this.homeWidgetDirective.keyDate;
  }

  public formatDuration(): string {
    const from = this.parseDate(this.settings.from);
    const to = this.parseDate(this.settings.to || this.keyDate);
    return this.locText(from, to);
  }

  public parseDate(data: string | Date): Date {
    if (typeof data == 'string') {
      return parseISO(data);
    }
    return data;
  }

  public locText(from: Date, to: Date): string {
    if (to && Math.abs(new Date().getTime() - to.getTime()) < 1000 * 3600 * 24) {
      // to = today -> special handling for delta formatting
      to = undefined;
    }

    if (from && !to) {
      // Special handling for nice 'Since X days ago' etc.
      const text = 'Starting from {delta}';
      return text.replace('{delta}', formatDistance(from, new Date(), { addSuffix: true }));
    }

    let key = 'records time';
    if (from) key += ' from';
    if (to) key += ' to';

    let text = HomeWidget.spanFmt[key];
    if (from) text = text?.replace('{from}', this.format(from));
    if (to) text = text?.replace('{to}', this.format(to));

    return text;
  }

  private format(date): string {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }
}
